import React, { useRef, useState, useEffect } from 'react';
import { createPopper } from '@popperjs/core';
import { createPortal } from 'react-dom';

export default function InfoTooltip({
  children,
  className = '',
  widthClass = 'w-44 sm:w-64',
}) {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const arrowRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;
  // 🧠 Close on outside click — added setTimeout to delay binding
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target) &&
        !triggerRef.current.contains(e.target)
      ) {
        setVisible(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible]);

  // set up Popper when tooltip becomes visible
  useEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) return;
    const popperInstance = createPopper(triggerRef.current, tooltipRef.current, {
      placement: 'top',
      strategy: 'fixed',
      modifiers: [
        { name: 'arrow', options: { element: arrowRef.current, padding: 8 } },
        { name: 'flip', options: { fallbackPlacements: ['bottom', 'right', 'left'] } },
        { name: 'preventOverflow', options: { boundary: 'viewport' } },
      ],
    });
    return () => popperInstance.destroy();
  }, [visible]);

  // the actual tooltip markup, portaled to <body>
  const tooltip = visible && createPortal(
    <div
      ref={tooltipRef}
      className={`
        ${widthClass}
        bg-primaryDark text-white p-3 rounded shadow-lg
        text-xs md:text-sm leading-tight z-[9999]
      `}
    >
      {children}
      {/* Arrow */}
      <div
        ref={arrowRef}
        className="absolute w-0 h-0 border-transparent border-solid
                   border-l-[6px] border-l-transparent
                   border-r-[6px] border-r-transparent
                   border-b-[6px] border-b-primaryDark
                   data-popper-placement^='bottom'?:hidden"
        data-popper-arrow
      />
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-flex cursor-pointer ml-1 ${className}`}
        onClick={(e) => {
          e.stopPropagation();
          setVisible((prev) => !prev);
        }}
        {...(!isTouchDevice && {
          onMouseEnter: () => setVisible(true),
          onMouseLeave: () => setVisible(false),
        })}
      >
        {/* your “i” icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5 text-primaryDark"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 
               2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 
               11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
      </div>
      {tooltip}
    </>
  );
}
